import BiologicalModelPage from '../../../Components/Biological/BiologicalModelPage';
import { segregationABM, segregationMetrics } from '../simulations/zoo/segregation';
import { epidemicGrid, epidemicSIR } from '../simulations/zoo/epidemic';
import { flockingBoids, flockingPredators } from '../simulations/zoo/flocking';

export function SegregationPage() {
    return (
        <BiologicalModelPage
            title="Schelling Segregation"
            subtitle="Emergent spatial clustering from local agent preferences"
            description="Agents on a lattice relocate when their neighborhood falls below a similarity threshold. Even modest individual preferences can produce striking large-scale spatial patterns — a classic demonstration of micro-to-macro emergence in EvolveX living systems."
            variants={[
                {
                    key: 'abm',
                    label: 'Agent Grid (ABM)',
                    equations: [
                        'U_i = \\mathbb{1}\\left[\\frac{\\text{same-neighbors}_i}{\\text{occupied-neighbors}_i} \\geq \\tau\\right]',
                    ],
                    description: 'Unhappy agents relocate to empty cells until local similarity exceeds threshold $\\tau$.',
                    simulate: segregationABM,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [
                        { key: 'density', label: 'Occupancy density', default: 0.65, min: 0.3, max: 0.9, step: 0.05 },
                        { key: 'minorityShare', label: 'Minority share', default: 0.35, min: 0.1, max: 0.5, step: 0.05 },
                        { key: 'similarityThreshold', label: 'Similarity threshold', default: 0.35, min: 0.1, max: 0.9, step: 0.05 },
                        { key: 'stepsPerFrame', label: 'Steps per frame', default: 8, min: 1, max: 20, step: 1 },
                    ],
                },
                {
                    key: 'metrics',
                    label: 'Cluster Formation',
                    equations: [
                        'H(t) = \\frac{1}{N}\\sum_i \\frac{\\text{same-neighbors}_i(t)}{\\text{occupied-neighbors}_i(t)}',
                    ],
                    description: 'Tracks homogeneity $H(t)$ as agents sort into emergent clusters over time.',
                    simulate: segregationMetrics,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [
                        { key: 'density', label: 'Occupancy density', default: 0.65, min: 0.3, max: 0.9, step: 0.05 },
                        { key: 'minorityShare', label: 'Minority share', default: 0.35, min: 0.1, max: 0.5, step: 0.05 },
                        { key: 'similarityThreshold', label: 'Similarity threshold', default: 0.5, min: 0.1, max: 0.9, step: 0.05 },
                        { key: 'stepsPerFrame', label: 'Steps per frame', default: 10, min: 1, max: 20, step: 1 },
                    ],
                },
            ]}
        />
    );
}

export function EpidemicPage() {
    return (
        <BiologicalModelPage
            title="Virus Spread (SIR)"
            subtitle="Lattice epidemic dynamics with traveling infection fronts"
            description="Susceptible, infected, and recovered agents interact on a spatial grid. Local contagion and recovery produce traveling infection fronts, spatial heterogeneity, and endemic plateaus beyond well-mixed compartment models."
            variants={[
                {
                    key: 'grid',
                    label: 'Spatial SIR Lattice',
                    equations: [
                        'P(S \\to I) = \\beta, \\quad P(I \\to R) = \\gamma',
                    ],
                    description: 'Infected cells transmit to susceptible Moore neighbors with probability $\\beta$ each step.',
                    simulate: epidemicGrid,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [
                        { key: 'initialInfected', label: 'Initial infected', default: 12, min: 1, max: 80, step: 1 },
                        { key: 'infectionRate', label: 'Infection rate beta', default: 0.35, min: 0.05, max: 0.9, step: 0.05 },
                        { key: 'recoveryRate', label: 'Recovery rate gamma', default: 0.08, min: 0.01, max: 0.3, step: 0.01 },
                        { key: 'stepsPerFrame', label: 'Steps per frame', default: 3, min: 1, max: 10, step: 1 },
                    ],
                },
                {
                    key: 'sir',
                    label: 'Outbreak Dynamics',
                    equations: [
                        '\\frac{dS}{dt} = -\\beta SI, \\quad \\frac{dI}{dt} = \\beta SI - \\gamma I, \\quad \\frac{dR}{dt} = \\gamma I',
                    ],
                    description: 'Classic compartmental SIR dynamics emerging from local agent interactions on the grid.',
                    simulate: epidemicSIR,
                    canvasSize: { width: 540, height: 540 },
                    parameters: [
                        { key: 'initialInfected', label: 'Initial infected', default: 8, min: 1, max: 80, step: 1 },
                        { key: 'infectionRate', label: 'Infection rate beta', default: 0.4, min: 0.05, max: 0.9, step: 0.05 },
                        { key: 'recoveryRate', label: 'Recovery rate gamma', default: 0.06, min: 0.01, max: 0.3, step: 0.01 },
                        { key: 'stepsPerFrame', label: 'Steps per frame', default: 2, min: 1, max: 10, step: 1 },
                    ],
                },
            ]}
        />
    );
}

export function FlockingPage() {
    return (
        <BiologicalModelPage
            title="Flocking (Boids)"
            subtitle="Self-organized swarm motion from local interaction rules"
            description="Autonomous agents follow separation, alignment, and cohesion rules to produce coherent collective motion. EvolveX renders classic boid swarms and pursuit-escape dynamics when predators are introduced."
            variants={[
                {
                    key: 'boids',
                    label: 'Classic Boids',
                    equations: [
                        '\\mathbf{v}_{new} = \\mathbf{v} + w_s \\mathbf{S} + w_a \\mathbf{A} + w_c \\mathbf{C}',
                    ],
                    description: 'Reynolds boids combine separation $\\mathbf{S}$, alignment $\\mathbf{A}$, and cohesion $\\mathbf{C}$ within perception radius.',
                    simulate: flockingBoids,
                    parameters: [
                        { key: 'count', label: 'Agent count', default: 120, min: 30, max: 250, step: 10 },
                        { key: 'perception', label: 'Perception radius', default: 48, min: 20, max: 100, step: 2 },
                        { key: 'separation', label: 'Separation weight', default: 1.4, min: 0.2, max: 3, step: 0.1 },
                        { key: 'alignment', label: 'Alignment weight', default: 0.9, min: 0.1, max: 2, step: 0.1 },
                        { key: 'cohesion', label: 'Cohesion weight', default: 0.7, min: 0.1, max: 2, step: 0.1 },
                        { key: 'maxSpeed', label: 'Max speed', default: 3.5, min: 1, max: 8, step: 0.25 },
                    ],
                },
                {
                    key: 'predators',
                    label: 'Predator-Prey Flocking',
                    equations: [
                        '\\mathbf{v}_{pred} \\propto \\arg\\min_{boid} \\|\\mathbf{x}_{pred} - \\mathbf{x}_{boid}\\|',
                    ],
                    description: 'Predators chase nearest boids while the flock maintains cohesion, producing pursuit-escape dynamics.',
                    simulate: flockingPredators,
                    parameters: [
                        { key: 'count', label: 'Boid count', default: 100, min: 30, max: 200, step: 10 },
                        { key: 'predatorCount', label: 'Predator count', default: 6, min: 1, max: 20, step: 1 },
                        { key: 'perception', label: 'Perception radius', default: 44, min: 20, max: 100, step: 2 },
                        { key: 'separation', label: 'Separation weight', default: 1.6, min: 0.2, max: 3, step: 0.1 },
                        { key: 'alignment', label: 'Alignment weight', default: 0.8, min: 0.1, max: 2, step: 0.1 },
                        { key: 'cohesion', label: 'Cohesion weight', default: 0.6, min: 0.1, max: 2, step: 0.1 },
                        { key: 'maxSpeed', label: 'Boid max speed', default: 3.2, min: 1, max: 8, step: 0.25 },
                        { key: 'predatorSpeed', label: 'Predator max speed', default: 4.2, min: 2, max: 10, step: 0.25 },
                    ],
                },
            ]}
        />
    );
}
